import { Component, inject, Input, signal } from '@angular/core';
import { arabicNameValidators, optionalName } from '../../../../validators/name-rules';
import { CompaniesService } from '../../../../services/companies/companies.service';
import { CompaniesFileMgt } from '../../../../services/companies/filemgt.companies.service';
import { Router } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { OneCompanyResponseData } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-edit-company-component',
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './edit-company-component.html',
  styleUrl: './edit-company-component.scss',
})
export class EditCompanyComponent {
  @Input() companyId!: number;

  private companyService = inject(CompaniesService);
  private companyFileMgt = inject(CompaniesFileMgt);
  private router = inject(Router);

  loading = signal<boolean>(false);
  loadingCompany = signal<boolean>(false);
  errorUpdatingCompany = signal<string | null>(null);

  selectedImage: File | null = null;
  imagePreview: string | null = null;
  existingImageFid: string | null = null;
  formSubmitted = false;
  isDragging = false;
  isActive = false;

  companyForm = new FormGroup({
    titleEn: new FormControl('', [Validators.minLength(2), Validators.maxLength(100)]),
    titleAr: new FormControl('', arabicNameValidators(100)),
    descriptionEn: new FormControl('', [Validators.maxLength(4000)]),
    descriptionAr: new FormControl('', [Validators.maxLength(4000)]),
    profileImageFid: new FormControl(''),
  });

  ngOnInit() {
    if (this.companyId) {
      this.loadCompany();
    }
  }

  loadCompany() {
    this.loadingCompany.set(true);
    this.companyService.getOneMFGCompany(this.companyId).subscribe({
      next: (company: OneCompanyResponseData) => {
        this.companyForm.patchValue({
          titleEn: company.titleEn,
          titleAr: company.titleAr || '',
          descriptionEn: company.descriptionEn || '',
          descriptionAr: company.descriptionAr || '',
          profileImageFid: company.profileImageFid || '',
        });
        this.existingImageFid = company.profileImageFid || null;
        this.loadingCompany.set(false);
      },
      error: (err) => {
        console.error('Error loading company:', err);
        this.loadingCompany.set(false);
        this.errorUpdatingCompany.set('Failed to load company. Please try again.');
      },
    });
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.processFile(file);

    input.value = '';
  }

  processFile(file: File) {
    if (!file.type.match(/image\/(png|jpe?g)/)) {
      alert($localize`:@@companyFormFileTypeError:Only PNG and JPG files are allowed`);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert($localize`:@@companyFormFileSizeError:File ${file.name} exceeds 5MB limit`);
      return;
    }

    this.selectedImage = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  removeImage() {
    this.selectedImage = null;
    this.imagePreview = null;
    this.existingImageFid = null;
    this.companyForm.patchValue({ profileImageFid: '' });
  }

  onSubmit() {
    this.formSubmitted = true;

    if (this.companyForm.invalid) {
      for (const key in this.companyForm.controls) {
        this.companyForm.get(key)?.markAsTouched();
      }
      return;
    }

    this.loading.set(true);
    this.errorUpdatingCompany.set(null);

    if (this.selectedImage) {
      this.companyFileMgt.uploadImage(this.selectedImage).subscribe({
        next: (result) => {
          this.updateCompany(result.fid);
        },
        error: (error) => {
          console.error('Error uploading image:', error);
          this.loading.set(false);
          this.errorUpdatingCompany.set('Failed to upload image. Please try again.');
        },
      });
    } else {
      this.updateCompany(this.existingImageFid || '');
    }
  }

  private updateCompany(imageFid: string) {
    const formValue = this.companyForm.getRawValue();
    const payload = {
      titleEn: optionalName(formValue.titleEn),
      titleAr: formValue.titleAr!.trim(),
      descriptionEn: formValue.descriptionEn || '',
      descriptionAr: formValue.descriptionAr || '',
      // isActive: formValue.isActive!,
      profileImageFid: imageFid,
    };

    this.companyService.updateCompany(this.companyId, payload).subscribe({
      next: () => {
        this.loading.set(false);
        this.router.navigate(['/admin/companies']);
      },
      error: (err) => {
        console.error('Error updating company:', err);
        this.loading.set(false);
        this.errorUpdatingCompany.set('Failed to update company. Please try again.');
      },
    });
  }

  onCancel() {
    this.router.navigate(['/admin/companies']);
  }
}
